/**
 * DynamicField dispatcher -- routes to the correct field component based on type.
 */
import React from 'react';
import type { ConnectorFieldSchema } from '../../../../types/connector-schema';
import type { FieldType, LoaderConfig } from '../../../../types/connector-schema';
import {
  TextField,
  PasswordField,
  NumberField,
  BooleanField,
  SelectField,
  TextareaField,
  FileField,
  MultiSelectCheckboxField,
  OAuthButtonField,
  OAuthPopulatedDropdownField,
  HiddenField,
  type DynamicFieldProps,
} from './fields';
import { getFieldEnhancement, type EnhancementProps } from './enhancements';
import {
  LoadAdvertisersButton,
  type Advertiser,
} from '../../form/LoadAdvertisersButton';
import { ChipMultiSelectDynamic } from '../../form/ChipMultiSelectDynamic';
import { SelectDynamic } from '../../form/SelectDynamic';

/**
 * True when the companion field this loader populates already holds a choice
 * (#1999). Reads ``loader_config.selection_field`` so the renderer does not
 * have to know which connector it is drawing; ``account_ids`` is the fallback
 * for Yahoo DSP's schema, which predates the declaration.
 */
export function loaderHasSelection(
  loaderConfig: LoaderConfig | undefined,
  allValues: Record<string, unknown>,
): boolean {
  const selectionField = loaderConfig?.selection_field ?? 'account_ids';
  const selected = allValues[selectionField];
  if (Array.isArray(selected)) return selected.length > 0;
  return selected !== undefined && selected !== null && selected !== '';
}

// Map field types to their components -- only includes uniform-prop fields.
// The two "cross-field" types (load_advertisers_button + multi_select_checkbox_dynamic)
// need access to a shared loader-results slice, so they're dispatched below.
const FIELD_TYPE_MAP: Partial<
  Record<FieldType, React.ComponentType<DynamicFieldProps>>
> = {
  text: TextField,
  password: PasswordField,
  number: NumberField,
  boolean: BooleanField,
  select: SelectField,
  textarea: TextareaField,
  file: FileField,
  json: TextareaField, // JSON fields use textarea
  multi_select_checkbox: MultiSelectCheckboxField,
  oauth_button: OAuthButtonField,
  oauth_populated_dropdown: OAuthPopulatedDropdownField,
  hidden: HiddenField,
};

interface DynamicFieldComponentProps {
  field: ConnectorFieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  onBatchFieldChange?: (updates: Record<string, unknown>) => void;
  error?: string;
  allValues: Record<string, unknown>;
  readOnly?: boolean;
  connectorType?: string;
  credentialFlags?: Record<string, boolean>;
  /**
   * Runtime results from LOAD_ADVERTISERS_BUTTON fields. Keyed by the loader
   * field's ``name``. MULTI_SELECT_CHECKBOX_DYNAMIC fields read from this
   * map via their ``loader_field`` pointer.
   */
  loaderResults?: Record<string, Advertiser[]>;
  /**
   * Callback fired when a LOAD_ADVERTISERS_BUTTON completes a fetch. The
   * host form typically writes the advertiser list into its ``loaderResults``
   * state under ``fieldName`` so the companion dynamic checkbox can render.
   */
  onLoaderResult?: (fieldName: string, advertisers: Advertiser[]) => void;
}

// Hoisted to module scope (react-hooks/static-components, #1123): the
// connector-specific enhancement component resolved by getFieldEnhancement()
// is dynamic (varies per connectorType/fieldName), so it's passed in as a
// prop here rather than being computed and used as a JSX tag inline in
// DynamicField's render body.
function EnhancementSlot({
  Component,
  ...props
}: { Component: React.ComponentType<EnhancementProps> } & EnhancementProps) {
  return <Component {...props} />;
}

export function DynamicField({
  field,
  value,
  onChange,
  onBatchFieldChange,
  error,
  allValues,
  readOnly,
  connectorType,
  credentialFlags,
  loaderResults,
  onLoaderResult,
}: DynamicFieldComponentProps) {
  const Enhancement = connectorType
    ? getFieldEnhancement(connectorType, field.name)
    : null;

  // Check if this credential field has a stored value on the server
  const hasStoredValue = field.is_credential && credentialFlags
    ? !!credentialFlags[`has_${field.name}`]
    : false;

  // Cross-field components -- dispatched before the uniform-prop map because
  // they require access to the host form's loaderResults slice.
  if (field.type === 'load_advertisers_button') {
    const endpoint = field.loader_config?.endpoint ?? '';
    const credentialFields = field.loader_config?.credential_fields ?? [];
    // formValues for this component is a flat string->string map of the form
    // values for the credential fields it depends on.
    const formValues: Record<string, string> = {};
    for (const f of credentialFields) {
      const v = allValues[f];
      formValues[f] = typeof v === 'string' ? v : v == null ? '' : String(v);
    }
    return (
      <LoadAdvertisersButton
        fieldName={field.name}
        endpoint={endpoint}
        credentialFields={credentialFields}
        formValues={formValues}
        onLoaded={(advertisers) => {
          onLoaderResult?.(field.name, advertisers);
        }}
        hasSelection={loaderHasSelection(field.loader_config, allValues)}
        sendsConnectorName={field.loader_config?.sends_connector_name}
      />
    );
  }

  if (field.type === 'multi_select_checkbox_dynamic') {
    const loaderName = field.loader_field ?? '';
    const options = loaderResults?.[loaderName] ?? [];
    const selected = Array.isArray(value)
      ? (value.filter((v) => typeof v === 'number') as number[])
      : [];
    const handleChange = (next: number[]) => {
      // Persist display names alongside ids so the Configuration page can show
      // saved advertisers without a live fetch. (#627)
      // splitFormData drops `selected_advertisers` for connectors whose schema
      // doesn't declare it, so this emit is safe for all connectors.
      const byId = new Map(options.map((o) => [o.id, o]));
      const selectedAdvertisers = next.map((id) => {
        const o = byId.get(id);
        return { id, name: o?.name ?? String(id), status: o?.status ?? '' };
      });
      if (onBatchFieldChange) {
        onBatchFieldChange({ [field.name]: next, selected_advertisers: selectedAdvertisers });
      } else {
        onChange(next);
      }
    };
    return (
      <ChipMultiSelectDynamic
        fieldName={field.name}
        options={options}
        value={selected}
        onChange={handleChange}
        label={field.label}
        help_text={field.help_text}
        disabled={readOnly}
      />
    );
  }

  // Single-valued loader-backed dropdown (#1999). Cross-field for the same
  // reason the dynamic checkbox above is: it reads the loader's results.
  if (field.type === 'select_dynamic') {
    const loaderName = field.loader_field ?? '';
    return (
      <SelectDynamic
        fieldName={field.name}
        options={loaderResults?.[loaderName] ?? []}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={onChange}
        emptyLabel={field.placeholder || 'No filter'}
        disabled={readOnly}
      />
    );
  }

  // Hidden fields render nothing -- short-circuit before wrapping in a fragment.
  if (field.type === 'hidden') {
    return null;
  }

  const Component = FIELD_TYPE_MAP[field.type] ?? TextField;

  return (
    <>
      <Component
        field={field}
        value={value}
        onChange={onChange}
        onBatchFieldChange={onBatchFieldChange}
        error={error}
        allValues={allValues}
        readOnly={readOnly}
        hasStoredValue={hasStoredValue}
      />
      {Enhancement && (
        <EnhancementSlot
          Component={Enhancement}
          connectorType={connectorType!}
          fieldName={field.name}
          formValues={allValues}
          onFieldChange={(name, val) => {
            if (name === field.name) {
              onChange(val);
            }
          }}
        />
      )}
    </>
  );
}
